import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    PublicClientApplication,
    InteractionRequiredAuthError,
    BrowserAuthError,
    LogLevel,
    version as msalVersion,
} from "@azure/msal-browser";
import "./SparkWidget.css";

const SPARK_ICON = "/assets/spark-widget.png";
const API_BASE = window.location.origin;
const REDIRECT_URI = `${window.location.origin}/auth.html`;

// Featured Sparks - shown at top of spark picker
const FEATURED_SPARKS = [
    { id: 20685, name: "⭐ Sphere", description: "Multi-modal AI assistant" },
    { id: 56658, name: "⭐ Transcript Summarizer", description: "Meeting insights & summaries" },
    { id: 58678, name: "⭐ Workplace Search", description: "Search Teams, Outlook, OneDrive & SharePoint" },
];

const msalConfig = {
    auth: {
        clientId: "52cb6025-bca2-447e-acdf-b7603c63bae1",
        authority:
            "https://login.microsoftonline.com/8f3e36ea-8039-4b40-81a7-7dc0599e8645",
        redirectUri: REDIRECT_URI,
        navigateToLoginRequestUrl: false,
        supportsNestedAppAuth: true,
    },
    cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
    },
    system: {
        allowRedirectInIframe: true,
        windowHashTimeout: 60000,
        iframeHashTimeout: 10000,
        loadFrameTimeout: 60000,
        asyncPopups: false,
        loggerOptions: {
            logLevel: LogLevel.Verbose,
            loggerCallback: (level, message, containsPii) => {
                if (!containsPii) {
                    console.log(`[MSAL ${level}]`, message);
                }
            },
        },
    },
};

const loginRequest = {
    scopes: ["openid", "profile", "email", "User.Read"],
    redirectUri: REDIRECT_URI,
    prompt: "select_account",
};

const tokenRequest = {
    scopes: ["User.Read"],
    redirectUri: REDIRECT_URI,
};

const msalInstance = new PublicClientApplication(msalConfig);

function formatAuthError(err) {
    if (!err) return "Unknown sign-in error.";

    const code = err.errorCode || err.code || err.name || "unknown_error";
    const msg = err.errorMessage || err.message || String(err);

    return `Sign-in failed.\nCode: ${code}\nMessage: ${msg}`;
}

async function ensureMsalReady() {
    if (!window.__spark_msal_initialized__) {
        console.log("[SparkWidget] Initializing MSAL...");
        console.log("[SparkWidget] MSAL version:", msalVersion);
        await msalInstance.initialize();
        window.__spark_msal_initialized__ = true;
        console.log("[SparkWidget] MSAL initialized.");
    }
}

async function bootstrapAccountFromCache() {
    try {
        const response = await msalInstance.handleRedirectPromise();
        if (response?.account) {
            console.log("[SparkWidget] handleRedirectPromise account:", response.account);
            msalInstance.setActiveAccount(response.account);
            return response.account;
        }
    } catch (err) {
        console.error("[SparkWidget] handleRedirectPromise error:", err);
    }

    const active = msalInstance.getActiveAccount();
    if (active) {
        console.log("[SparkWidget] Active account found:", active);
        return active;
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
        console.log("[SparkWidget] Cached account found:", accounts[0]);
        msalInstance.setActiveAccount(accounts[0]);
        return accounts[0];
    }

    console.log("[SparkWidget] No cached account found.");
    return null;
}

async function getAccessToken() {
    const account =
        msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

    if (!account) {
        throw new Error("Not authenticated");
    }

    try {
        const result = await msalInstance.acquireTokenSilent({
            ...tokenRequest,
            account,
        });
        return result.accessToken;
    } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
            console.warn("[SparkWidget] Silent token failed, trying popup...");
            const popupResult = await msalInstance.acquireTokenPopup({
                ...tokenRequest,
            });

            if (popupResult?.account) {
                msalInstance.setActiveAccount(popupResult.account);
            }

            return popupResult.accessToken;
        }
        throw err;
    }
}

function SendIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
            <path
                d="M22 2L11 13"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M22 2L15 22L11 13L2 9L22 2Z"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function ChevronIcon() {
    return (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
                d="M2 4L6 8L10 4"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
            />
        </svg>
    );
}

function LoadingDots() {
    return (
        <span className="sw-loading-dots" aria-label="Thinking…">
            <span />
            <span />
            <span />
        </span>
    );
}

function MessageBubble({ msg }) {
    const isUser = msg.role === "user";

    return (
        <div className={`sw-bubble-wrap ${isUser ? "sw-bubble-user" : "sw-bubble-ai"}`}>
            {!isUser && (
                <div className="sw-avatar">
                    <img src={SPARK_ICON} alt="Spark" />
                </div>
            )}
            <div className={`sw-bubble ${isUser ? "sw-bubble--user" : "sw-bubble--ai"}`}>
                {msg.loading ? (
                    <LoadingDots />
                ) : isUser ? (
                    <span className="sw-bubble-text">{msg.content}</span>
                ) : (
                    <div 
                        className="sw-bubble-text" 
                        dangerouslySetInnerHTML={{ __html: msg.content }}
                    />
                )}
            </div>
        </div>
    );
}

function LoginScreen({ onLogin, isLoggingIn, loginError }) {
    return (
        <div className="sw-login">
            <div className="sw-login-logo">
                <img src={SPARK_ICON} alt="Spark AI" />
            </div>
            <h2 className="sw-login-title">Spark AI</h2>
            <p className="sw-login-sub">
                Sign in with your S&amp;P Global account to start chatting.
            </p>

            {loginError && (
                <div
                    style={{
                        marginBottom: "12px",
                        color: "#b42318",
                        fontSize: "13px",
                        textAlign: "center",
                        maxWidth: "340px",
                        whiteSpace: "pre-wrap",
                        lineHeight: "1.45",
                    }}
                >
                    {loginError}
                </div>
            )}

            <button className="sw-login-btn" onClick={onLogin} disabled={isLoggingIn}>
                {isLoggingIn ? "Signing in…" : "Sign in"}
            </button>
        </div>
    );
}

function SparkPicker({ sparks, selectedSpark, onSelect, loading, error }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const getLabel = () => {
        if (loading) return "Loading Sparks…";
        if (error) return "Error loading Sparks";
        if (selectedSpark) return selectedSpark.name;
        if (sparks.length === 0) return "No Sparks available";
        return "Choose a Spark";
    };

    return (
        <div className="sw-picker" ref={ref}>
            <button
                className="sw-picker-btn"
                onClick={() => setOpen((v) => !v)}
                disabled={loading || error || sparks.length === 0}
            >
                <span className="sw-picker-dot" />
                <span className="sw-picker-label">{getLabel()}</span>
                <ChevronIcon />
            </button>

            {open && sparks.length > 0 && (
                <div className="sw-picker-menu">
                    {sparks.map((s) => (
                        <button
                            key={s.id}
                            className={`sw-picker-item ${selectedSpark?.id === s.id ? "active" : ""
                                }`}
                            onClick={() => {
                                onSelect(s);
                                setOpen(false);
                            }}
                        >
                            <span className="sw-picker-dot" />
                            <span>{s.name}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function SparkWidget() {
    const theme =
        new URLSearchParams(window.location.search).get("theme") || "light";

    const [authReady, setAuthReady] = useState(false);
    const [user, setUser] = useState(null);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [loginError, setLoginError] = useState("");

    const [sparks, setSparks] = useState([]);
    const [sparksLoading, setSparksLoading] = useState(false);
    const [sparksError, setSparksError] = useState("");
    const [selectedSpark, setSelectedSpark] = useState(null);

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);

    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const handler = (event) => {
            // Listen for MSAL redirect bridge responses
            if (event.data?.type === "msal") {
                console.log("[SparkWidget] MSAL redirect bridge message received:", event.data);
            }

            if (event.origin !== window.location.origin) return;
            if (event.data?.type === "SPARK_AUTH_POPUP_REACHED") {
                console.log("[SparkWidget] Popup reached auth.html:", event.data);
            }
        };

        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, []);

    useEffect(() => {
        let mounted = true;

        (async () => {
            try {
                await ensureMsalReady();
                const account = await bootstrapAccountFromCache();
                if (mounted && account) {
                    setUser(account);
                }
            } catch (err) {
                console.error("[SparkWidget] Bootstrap error:", err);
            } finally {
                if (mounted) {
                    setAuthReady(true);
                }
            }
        })();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!user) return;

        (async () => {
            setSparksLoading(true);
            setSparksError("");

            try {
                // Extract sparkId from URL first (from data-spark-id in embed script)
                const hashParts = window.location.hash.split('?');
                const urlSparkId = hashParts.length > 1 
                    ? new URLSearchParams(hashParts[1]).get("sparkId")
                    : new URLSearchParams(window.location.search).get("sparkId");

                console.log("[SparkWidget] URL sparkId:", urlSparkId);

                // If sparkId is provided, skip API call and only use that spark
                if (urlSparkId) {
                    console.log("[SparkWidget] sparkId provided in URL - skipping full catalog fetch");

                    // Check if it's a featured spark first
                    const featuredMatch = FEATURED_SPARKS.find((s) => String(s.id) === String(urlSparkId));

                    if (featuredMatch) {
                        console.log(`[SparkWidget] ✅ Using featured spark: ${featuredMatch.id} - ${featuredMatch.name}`);
                        setSparks([featuredMatch]);
                        setSelectedSpark(featuredMatch);
                        setSparksLoading(false);
                        return;
                    }

                    // If not featured, create a placeholder entry (will work with any valid spark ID)
                    console.log(`[SparkWidget] Creating placeholder for spark ID: ${urlSparkId}`);
                    const placeholder = {
                        id: parseInt(urlSparkId, 10),
                        name: `Spark ${urlSparkId}`,
                        description: "Loading..."
                    };

                    setSparks([placeholder]);
                    setSelectedSpark(placeholder);
                    setSparksLoading(false);
                    return;
                }

                // No sparkId provided - fetch full catalog
                console.log("[SparkWidget] No sparkId in URL - fetching full spark catalog");
                console.log("[SparkWidget] Fetching sparks from:", `${API_BASE}/api/widget/sparks`);

                // Note: Authorization temporarily disabled on server for testing
                const token = await getAccessToken();
                const res = await fetch(`${API_BASE}/api/widget/sparks`, {
                    method: 'GET',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                });

                console.log("[SparkWidget] Sparks API status:", res.status);

                if (!res.ok) {
                    const errorText = await res.text();
                    console.error("[SparkWidget] Sparks API error:", res.status, errorText);
                    throw new Error(`Failed to load sparks: ${res.status} - ${errorText}`);
                }

                const data = await res.json();
                console.log("[SparkWidget] Sparks API response:", data);
                console.log("[SparkWidget] Response keys:", Object.keys(data));

                // Parse Spark Catalog response - handle different formats
                let list = [];

                // Check for direct sparks array at root (current API format)
                if (data.sparks && Array.isArray(data.sparks)) {
                    console.log("[SparkWidget] Found sparks array at root:", data.sparks.length);

                    list = data.sparks.map(s => ({
                        id: s.spark_id,
                        name: s.spark_name || `Spark ${s.spark_id}`,
                        description: s.spark_description || ""
                    }));
                } 
                else if (data.params && data.params.result && data.params.result.sparks) {
                    // Wrapped format: { params: { result: { sparks: [...] } } }
                    const rawSparks = data.params.result.sparks;
                    console.log("[SparkWidget] Found sparks in params.result.sparks:", rawSparks.length);

                    list = rawSparks.map(s => ({
                        id: s.spark_id,
                        name: s.spark_name || `Spark ${s.spark_id}`,
                        description: s.spark_description || ""
                    }));
                } 
                else {
                    // Last resort fallback
                    console.warn("[SparkWidget] Unknown response structure, trying direct array");
                    const rawList = data.items || data || [];
                    if (Array.isArray(rawList)) {
                        list = rawList.map(s => ({
                            id: s.spark_id || s.id,
                            name: s.spark_name || s.name || `Spark ${s.spark_id || s.id}`,
                            description: s.spark_description || s.description || ""
                        }));
                    }
                }

                console.log("[SparkWidget] Parsed sparks:", list.length);
                if (list.length > 0) {
                    console.log("[SparkWidget] First spark:", list[0]);
                }

                // Prepend featured sparks to the top of the list (if not already present)
                const featuredIds = new Set(FEATURED_SPARKS.map(s => s.id));
                const nonFeatured = list.filter(s => !featuredIds.has(s.id));
                const finalList = [...FEATURED_SPARKS, ...nonFeatured];

                console.log("[SparkWidget] Final spark list with featured:", finalList.length);
                console.log("[SparkWidget] Featured sparks:", FEATURED_SPARKS.map(s => `${s.id}: ${s.name}`));

                if (finalList.length === 0) {
                    console.warn("[SparkWidget] No sparks available after merging featured sparks");
                    setSparksError("No Sparks available");
                } else {
                    setSparks(finalList);

                    // When fetching full catalog (no URL sparkId), default to first featured spark
                    console.log("[SparkWidget] Full catalog loaded - defaulting to first featured spark");
                    setSelectedSpark(finalList[0] || null);
                }
            } catch (err) {
                console.error("[SparkWidget] sparks load error:", err);
                setSparksError(err.message || "Failed to load Sparks");
            } finally {
                setSparksLoading(false);
            }
        })();
    }, [user]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleLogin = useCallback(async () => {
        setIsLoggingIn(true);
        setLoginError("");

        try {
            await ensureMsalReady();

            console.log("[SparkWidget] Starting loginPopup with redirectUri:", REDIRECT_URI);

            const result = await msalInstance.loginPopup({
                ...loginRequest,
            });

            console.log("[SparkWidget] loginPopup result:", result);

            if (result?.account) {
                msalInstance.setActiveAccount(result.account);
                setUser(result.account);
            } else {
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    msalInstance.setActiveAccount(accounts[0]);
                    setUser(accounts[0]);
                } else {
                    throw new Error("Login completed but no account was found in MSAL cache.");
                }
            }
        } catch (err) {
            console.error("[SparkWidget] loginPopup error:", err);

            if (
                err instanceof BrowserAuthError ||
                err?.errorCode === "timed_out" ||
                err?.errorCode === "monitor_window_timeout"
            ) {
                setLoginError(
                    `${formatAuthError(err)}\n\nThe popup returned to auth.html, but it lost window.opener.\nThis means the browser or host page isolated the popup, so MSAL popup login cannot complete.\n\nNext fix:\n- if using MSAL v5, use the redirect bridge\n- remove COOP: same-origin\n- use COOP same-origin-allow-popups or restrict-properties`
                );
            } else {
                setLoginError(formatAuthError(err));
            }
        } finally {
            setIsLoggingIn(false);
        }
    }, []);

    const handleSend = useCallback(async () => {
        const text = input.trim();
        if (!text || sending || !selectedSpark) return;

        const userId = Date.now();
        const loadingId = userId + 1;

        setMessages((prev) => [
            ...prev,
            { role: "user", content: text, id: userId },
            { role: "assistant", content: "", id: loadingId, loading: true },
        ]);

        setInput("");
        setSending(true);

        try {
            const history = messages
                .filter((m) => !m.loading)
                .map((m) => ({ role: m.role, content: m.content }));

            console.log("[SparkWidget] Sending chat message to sparkId:", selectedSpark.id);

            const token = await getAccessToken();
            const res = await fetch(`${API_BASE}/api/widget/chat`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    sparkId: selectedSpark.id.toString(),
                    message: text,
                    history,
                }),
            });

            console.log("[SparkWidget] Chat API status:", res.status);

            if (!res.ok) {
                const errorText = await res.text();
                console.error("[SparkWidget] Chat API error:", res.status, errorText);
                throw new Error(`Chat error: ${res.status} - ${errorText}`);
            }

            const data = await res.json();
            console.log("[SparkWidget] Chat API response:", data);
            console.log("[SparkWidget] Response keys:", Object.keys(data));

            const reply = data.message || data.content || data.reply || "No response.";
            console.log("[SparkWidget] Extracted reply (length):", reply.length);

            setMessages((prev) =>
                prev.map((m) =>
                    m.id === loadingId ? { ...m, content: reply, loading: false } : m
                )
            );
        } catch (err) {
            console.error("[SparkWidget] Chat error:", err);
            console.error("[SparkWidget] Error type:", err.constructor.name);
            console.error("[SparkWidget] Error message:", err.message);
            console.error("[SparkWidget] Error stack:", err.stack);

            setMessages((prev) =>
                prev.map((m) =>
                    m.id === loadingId
                        ? {
                            ...m,
                            content: `Error: ${err.message || "Something went wrong. Please try again."}`,
                            loading: false,
                        }
                        : m
                )
            );
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    }, [input, sending, selectedSpark, messages]);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClose = () => {
        const targetOrigin = document.referrer ? new URL(document.referrer).origin : window.location.origin;
        window.parent.postMessage("SPARK_WIDGET_CLOSE", targetOrigin);
    };

    if (!authReady) {
        return (
            <div className={`sw-root sw-theme-${theme}`}>
                <div className="sw-splash">
                    <div className="sw-splash-inner">
                        <div className="sw-splash-logo">
                            <img src={SPARK_ICON} alt="Spark" />
                        </div>
                        <div className="sw-splash-ring" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`sw-root sw-theme-${theme}`}>
            <header className="sw-header">
                <div className="sw-header-left">
                    <div className="sw-header-logo">
                        <img src={SPARK_ICON} alt="Spark" />
                    </div>
                    <span className="sw-header-title">Spark AI</span>
                    {user && <span className="sw-header-badge">Connected</span>}
                </div>

                <button className="sw-close-btn" onClick={handleClose} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                            d="M1 1L13 13M13 1L1 13"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                        />
                    </svg>
                </button>
            </header>

            {!user ? (
                <LoginScreen
                    onLogin={handleLogin}
                    isLoggingIn={isLoggingIn}
                    loginError={loginError}
                />
            ) : (
                <>
                    <div className="sw-picker-bar">
                        <SparkPicker
                            sparks={sparks}
                            selectedSpark={selectedSpark}
                            onSelect={setSelectedSpark}
                            loading={sparksLoading}
                            error={sparksError}
                        />
                    </div>

                    <div className="sw-messages" role="log" aria-live="polite">
                        {messages.length === 0 && (
                            <div className="sw-empty">
                                <div className="sw-empty-icon">
                                    <img src={SPARK_ICON} alt="Spark" />
                                </div>
                                <p>How can I help you today?</p>
                                {selectedSpark && (
                                    <span className="sw-empty-spark">Using: {selectedSpark.name}</span>
                                )}
                            </div>
                        )}

                        {messages.map((m) => (
                            <MessageBubble key={m.id} msg={m} />
                        ))}

                        <div ref={bottomRef} />
                    </div>

                    <div className="sw-input-bar">
                        <textarea
                            ref={inputRef}
                            className="sw-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={
                                selectedSpark
                                    ? `Ask ${selectedSpark.name}…`
                                    : "Select a Spark to begin…"
                            }
                            rows={1}
                            disabled={sending || !selectedSpark}
                            aria-label="Message input"
                        />
                        <button
                            className="sw-send-btn"
                            onClick={handleSend}
                            disabled={!input.trim() || sending || !selectedSpark}
                            aria-label="Send"
                        >
                            <SendIcon />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}